<template>
    <div
        id="stickyTopBar"
        class="xxs:h-12 xxs:px-3 sticky top-[var(--app-banner-offset,0px)] z-40 flex h-10 shrink-0 items-center border-b border-gray-200 bg-gray-50 shadow-xs sm:h-14 sm:px-4 xl:px-6"
    >
        <div class="flex flex-1 self-stretch">
            <TopbarSearch />

            <div class="xxs:gap-x-3 flex items-center gap-x-2 sm:gap-x-4 xl:gap-x-6">
                <div aria-hidden="true" class="xxs:h-10 h-8 w-px bg-gray-200 sm:h-12" />

                <button
                    @click="isCalculatorOpen = true"
                    class="inline-flex size-8 shrink-0 cursor-pointer items-center justify-center text-gray-400 hover:text-gray-500 sm:size-9"
                    type="button"
                    title="Calculator"
                >
                    <span class="sr-only">Calculator</span>
                    <CalculatorIcon aria-hidden="true" class="size-5 sm:size-6" />
                </button>
                <div aria-hidden="true" class="xxs:h-10 h-8 w-px bg-gray-200 sm:h-12" />

                <div class="relative flex shrink-0 items-center self-center">
                    <button
                        ref="notificationButtonRef"
                        @click="toggleNotifications"
                        class="relative inline-flex size-8 cursor-pointer items-center justify-center text-gray-400 hover:text-gray-500 sm:size-9"
                        type="button"
                    >
                        <span class="sr-only">View notifications</span>
                        <BellIcon aria-hidden="true" class="size-5 sm:size-6" />
                        <span
                            v-if="unreadCount > 0"
                            class="absolute -top-0.5 -right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white"
                        >
                            {{ unreadCount > 9 ? '9+' : unreadCount }}
                        </span>
                    </button>

                    <Teleport to="body">
                        <div v-if="showNotifications" class="fixed inset-0 z-[400]" @click="showNotifications = false" />

                        <div
                            v-if="showNotifications"
                            class="fixed z-[410] w-[min(20rem,calc(100vw-1.5rem))] rounded-lg border border-gray-200 bg-white shadow-lg"
                            :style="notificationMenuStyle"
                        >
                            <div class="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                                <span class="text-sm font-semibold text-gray-800">Notifications</span>
                                <div v-if="notifications.length" class="flex items-center gap-3">
                                    <button
                                        v-if="unreadCount > 0"
                                        @click="markAllAsRead"
                                        class="text-xs text-indigo-600 hover:underline"
                                    >
                                        Mark all read
                                    </button>
                                    <button
                                        @click="deleteAllNotifications"
                                        class="text-xs text-red-600 hover:underline"
                                    >
                                        Clear all
                                    </button>
                                </div>
                            </div>

                            <div class="max-h-72 overflow-y-auto">
                                <div v-if="!notifications.length" class="px-4 py-8 text-center text-sm text-gray-400">
                                    No notifications
                                </div>
                                <div
                                    v-for="n in notifications"
                                    :key="n.id"
                                    @click="handleNotificationClick(n)"
                                    :class="n.read_at ? 'bg-white' : 'bg-indigo-50'"
                                    class="flex cursor-pointer items-start gap-2 border-b border-gray-50 px-4 py-3 hover:bg-gray-50"
                                >
                                    <div class="min-w-0 flex-1">
                                        <p class="text-sm font-medium text-gray-800">{{ n.data?.message }}</p>
                                        <p v-if="n.data?.type === 'wholesaler_order_request'" class="mt-0.5 text-xs text-gray-500">
                                            {{ n.data?.items_count }} items · Rs. {{ n.data?.total_amount }}
                                        </p>
                                        <p
                                            v-else-if="n.data?.type?.startsWith('billing_') && (n.data?.invoice_number || n.data?.amount || n.data?.due_on)"
                                            class="mt-0.5 text-xs text-gray-500"
                                        >
                                            {{ n.data?.invoice_number }}<template v-if="n.data?.amount"> · Rs {{ Number(n.data.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</template><template v-if="n.data?.due_on"> · due {{ n.data.due_on }}</template>
                                        </p>
                                        <p v-else-if="n.data?.transfer_number" class="mt-0.5 text-xs text-gray-500">
                                            {{ n.data?.transfer_number }} · {{ n.data?.from_branch_name }} → {{ n.data?.to_branch_name }}
                                        </p>
                                        <p class="mt-1 text-[11px] text-gray-400">{{ formatTime(n.created_at) }}</p>
                                    </div>
                                    <div class="flex shrink-0 items-center gap-0.5" @click.stop>
                                        <button
                                            v-if="!n.read_at"
                                            type="button"
                                            title="Mark as read"
                                            @click="markAsRead(n.id)"
                                            class="rounded p-1 text-gray-400 hover:bg-indigo-100 hover:text-indigo-600"
                                        >
                                            <CheckIcon class="size-4" aria-hidden="true" />
                                            <span class="sr-only">Mark as read</span>
                                        </button>
                                        <button
                                            type="button"
                                            title="Delete"
                                            @click="deleteNotification(n.id)"
                                            class="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                                        >
                                            <TrashIcon class="size-4" aria-hidden="true" />
                                            <span class="sr-only">Delete</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="border-t border-gray-100 px-4 py-2 text-center">
                                <span class="text-xs text-gray-400">{{ notifications.length }} notifications</span>
                            </div>
                        </div>
                    </Teleport>
                </div>

                <div aria-hidden="true" class="xxs:h-10 h-8 w-px bg-gray-200 sm:h-12" />

                <Menu as="div" class="relative shrink-0">
                    <MenuButton
                        ref="profileButtonRef"
                        class="inline-flex cursor-pointer items-center"
                        @click="updateProfileMenuPosition"
                    >
                        <span class="sr-only">Open user menu</span>
                        <img alt="User Image" class="size-6 rounded-full bg-gray-50 sm:size-8" :src="previewImage" />
                        <span class="hidden lg:flex lg:items-center">
                            <span aria-hidden="true" class="ml-4 text-sm/6 font-semibold text-gray-900">{{ auth.user.name }}</span>
                            <ChevronDownIcon aria-hidden="true" class="ml-2 size-5 text-gray-400" />
                        </span>
                    </MenuButton>
                    <Teleport to="body">
                        <transition
                            enter-active-class="transition ease-out duration-100"
                            enter-from-class="transform opacity-0 scale-95"
                            enter-to-class="transform opacity-100 scale-100"
                            leave-active-class="transition ease-in duration-75"
                            leave-from-class="transform opacity-100 scale-100"
                            leave-to-class="transform opacity-0 scale-95"
                        >
                            <MenuItems
                                class="fixed z-[410] w-56 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-gray-900/5 focus:outline-hidden dark:bg-zinc-800 dark:shadow-xl dark:ring-white/15 dark:divide-zinc-700"
                                :style="profileMenuStyle"
                            >
                                <div class="py-1">
                                    <MenuItem
                                        v-for="item in userNavigation[0]"
                                        :key="item.title"
                                        as="template"
                                        v-slot="{ active }"
                                    >
                                        <RouterLink
                                            :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-3 py-2 text-sm font-medium']"
                                            :to="{ name: item.name }"
                                            :data-tour="item.tourAttr || undefined"
                                            @click="processMenuClick(item.title)"
                                        >
                                            <component
                                                :is="item.icon"
                                                :class="[active ? 'text-gray-500' : '', 'mr-2 size-5 shrink-0 text-gray-400']"
                                                aria-hidden="true"
                                            />
                                            {{ item.title }}
                                        </RouterLink>
                                    </MenuItem>
                                </div>
                                <div class="py-1">
                                    <MenuItem v-slot="{ active }" as="template">
                                        <button
                                            type="button"
                                            :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex w-full items-center px-3 py-1 text-sm font-medium']"
                                            @click="toggleAppearance"
                                        >
                                            <MoonIcon
                                                v-if="!isDark"
                                                :class="[active ? 'text-gray-500' : '', 'mr-2 size-5 text-gray-400']"
                                                aria-hidden="true"
                                            />
                                            <SunIcon
                                                v-else
                                                :class="[active ? 'text-gray-500' : '', 'mr-2 size-5 text-gray-400']"
                                                aria-hidden="true"
                                            />
                                            {{ isDark ? 'Light mode' : 'Dark mode' }}
                                        </button>
                                    </MenuItem>
                                </div>
                                <div class="py-1">
                                    <MenuItem v-for="item in userNavigation[1]" :key="item.title" v-slot="{ active }">
                                        <a
                                            :class="[active ? 'bg-gray-100 text-gray-900' : 'text-gray-700', 'group flex items-center px-3 py-1 text-sm font-medium']"
                                            :href="item.href"
                                            @click="processMenuClick(item.title)"
                                        >
                                            <component
                                                :is="item.icon"
                                                :class="[active ? 'text-gray-500' : '', 'mr-2 size-5 text-gray-400']"
                                                aria-hidden="true"
                                            />
                                            {{ item.title }}
                                        </a>
                                    </MenuItem>
                                </div>
                            </MenuItems>
                        </transition>
                    </Teleport>
                </Menu>
            </div>
        </div>
    </div>

    <Calculator
        v-if="isCalculatorOpen"
        @close-modal="closeCalculator"
        class="fixed inset-0 z-50 bg-white p-4 shadow-lg"
    />
</template>

<script setup>
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { BellIcon, CheckIcon, Cog6ToothIcon, MoonIcon, PowerIcon, QuestionMarkCircleIcon, SunIcon, TrashIcon, CalculatorIcon } from '@heroicons/vue/24/outline';
import { ChevronDownIcon } from '@heroicons/vue/20/solid';
import { useAuthStore } from '@/stores/auth-store.js';
import apiClient from '@/utils/axios.js';
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import Calculator from '@/components/Portal/Calculator/Calculator.vue';
import TopbarSearch from '@/components/Layout/TopbarSearch.vue';
import { useNotifications } from '@/composables/useNotifications.js';
import { useConfigurationStore } from '@/stores/configuration-store.js';
import { useAppearance } from '@/composables/useAppearance.js';

const emit = defineEmits(['loggingOut', 'loggingOutComplete']);
const router = useRouter();
const auth = useAuthStore();
const configStore = useConfigurationStore();
configStore.loadFromCache();

const { isDark, updateAppearance } = useAppearance();

const avatar = ref(auth.user.avatar);
const defaultAvatar =
    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80';
const previewImage = ref(avatar.value ? avatar.value : defaultAvatar);

const userNavigation = [
    [
        { title: 'Help & tours', name: 'help', icon: QuestionMarkCircleIcon, tourAttr: 'topbar-help-link' },
        { title: 'Settings', name: 'settings', icon: Cog6ToothIcon },
    ],
    [{ title: 'Sign out', href: '#', icon: PowerIcon }],
];

const showNotifications = ref(false);
const notificationButtonRef = ref(null);
const notificationMenuStyle = ref({ top: '0px', right: '0px' });
const profileButtonRef = ref(null);
const profileMenuStyle = ref({ top: '0px', right: '0px' });

const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllNotifications,
} = useNotifications();

const formatTime = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const profileButtonEl = () => {
    const raw = profileButtonRef.value;
    return raw?.$el ?? raw ?? null;
};

const updateProfileMenuPosition = async () => {
    await nextTick();
    const el = profileButtonEl();
    if (!el?.getBoundingClientRect) return;
    const rect = el.getBoundingClientRect();
    profileMenuStyle.value = {
        top: `${Math.round(rect.bottom + 8)}px`,
        right: `${Math.max(8, Math.round(window.innerWidth - rect.right))}px`,
    };
};

const updateNotificationPosition = async () => {
    await nextTick();
    const el = notificationButtonRef.value;
    if (!el?.getBoundingClientRect) return;
    const rect = el.getBoundingClientRect();
    const right = Math.max(8, Math.round(window.innerWidth - rect.right));
    notificationMenuStyle.value = {
        top: `${Math.round(rect.bottom + 12)}px`,
        right: `${right}px`,
    };
};

const toggleNotifications = async () => {
    showNotifications.value = !showNotifications.value;
    if (showNotifications.value) {
        await updateNotificationPosition();
    }
};

const handleNotificationClick = (n) => {
    markAsRead(n.id);
    showNotifications.value = false;
    if (n.data?.url) {
        router.push(n.data.url);
    }
};

const onNotificationWindowChange = () => {
    if (showNotifications.value) {
        updateNotificationPosition();
    }
    updateProfileMenuPosition();
};

const syncUserAppearance = (fields) => {
    if (!auth.userData) {
        return;
    }

    try {
        const data = typeof auth.userData === 'string' ? JSON.parse(auth.userData) : { ...auth.userData };
        Object.assign(data, fields);
        auth.userData = JSON.stringify(data);
        localStorage.setItem('userData', auth.userData);
    } catch (error) {
        // Error handled silently
    }
};

const toggleAppearance = async () => {
    const next = isDark.value ? 'light' : 'dark';
    updateAppearance(next);
    syncUserAppearance({ mode: next });

    if (!auth.isLoggedIn || !auth.getOnlineStatus) {
        return;
    }

    try {
        await apiClient.post('/user/appearanceSettings', {
            props: ['mode'],
            mode: next,
        });
    } catch (error) {
        // Local preference already applied; server sync can retry later
    }
};

const processMenuClick = async (name) => {
    if (name === 'Sign out') {
        emit('loggingOut');
        await auth.processLogout();
        emit('loggingOutComplete');
    }
};

const isCalculatorOpen = ref(false);
const closeCalculator = () => (isCalculatorOpen.value = false);

onMounted(() => {
    fetchNotifications();
    window.addEventListener('resize', onNotificationWindowChange);
    window.addEventListener('scroll', onNotificationWindowChange, true);
});

onUnmounted(() => {
    window.removeEventListener('resize', onNotificationWindowChange);
    window.removeEventListener('scroll', onNotificationWindowChange, true);
});
</script>

<style scoped></style>
